import {
	Button,
	Flex,
	Icon,
	Select,
	Text,
	TextField,
	useTheme,
} from "@repo/ui";
import { useState } from "react";
import { Dialog, Modal } from "react-aria-components";
import { useStyles } from "purse-styles";
import { payorDropdownOptions } from "@repo/config/payors";
import { getTenantManifest } from "@repo/config/tenants";
import { Insurance } from "../db/schema";
import { getTenantSlug } from "../tenant";

type EditInsuranceModalProps = {
	insurance: Insurance;
	isOpen: boolean;
	onClose: () => void;
	onSave: (data: {
		payor: string;
		memberId: string;
		planType: string | undefined;
		planName: string | undefined;
		specialistRefsReqPriorAuth: boolean | undefined;
		payorPhoneNumber: string | undefined;
	}) => Promise<void>;
	isSaving: boolean;
	title?: string;
	onDelete?: () => Promise<void>;
};

/**
 * The payor select for the tenant this browser is scoped to. Empty outside a
 * resolved tenant, where AppRoutes renders the tenant selector instead.
 *
 * Derived from the tenant's payor registry, so `value` is a canonical payor by
 * construction — it used to be three hand-written lists, and Demo was handed
 * HHB's by mistake.
 */
function payorOptions() {
	const tenantSlug = getTenantSlug();
	return [
		{ value: "", label: "Select payor" },
		...(tenantSlug
			? payorDropdownOptions(getTenantManifest(tenantSlug).clinic.payors)
			: []),
	];
}

const PLAN_TYPES = [
	{ value: "", label: "Unknown" },
	{ value: "PPO", label: "PPO" },
	{ value: "HMO", label: "HMO" },
];

const PRIOR_AUTH_OPTIONS = [
	{
		value: "",
		label: "Unknown",
		icon: "Shield" as const,
		iconColor: "sand9" as const,
	},
	{
		value: "true",
		label: "Yes",
		icon: "Shield" as const,
		iconColor: "amber11" as const,
	},
	{
		value: "false",
		label: "No",
		icon: "Shield" as const,
		iconColor: "green11" as const,
	},
];

const validatePhone = (phone: string): string | undefined => {
	if (!phone.trim()) return undefined;
	const cleanPhone = phone.replace(/\D/g, "");
	if (cleanPhone.length !== 10) {
		return "Phone must be 10 digits";
	}
	return undefined;
};

export const EditInsuranceModal: React.FC<EditInsuranceModalProps> = ({
	insurance,
	isOpen,
	onClose,
	onSave,
	isSaving,
	title = "Edit Insurance",
	onDelete,
}) => {
	const theme = useTheme();

	const [payor, setPayor] = useState(insurance.payor ?? "");
	const [memberId, setMemberId] = useState(insurance.memberId ?? "");
	const [planType, setPlanType] = useState(insurance.type ?? "");
	const [planName, setPlanName] = useState(insurance.planName ?? "");
	const [payorPhoneNumber, setPayorPhoneNumber] = useState(
		insurance.phone ?? "",
	);
	const [specialistRefsReqPriorAuth, setSpecialistRefsReqPriorAuth] =
		useState<string>(
			insurance.specialistRefsReqPriorAuth === true
				? "true"
				: insurance.specialistRefsReqPriorAuth === false
					? "false"
					: "",
		);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const modalClassName = useStyles({
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		zIndex: 1000,
	});

	const dialogClassName = useStyles({
		backgroundColor: theme.colors.sand1,
		borderRadius: theme.borderRadiuses.lg,
		width: "90vw",
		maxWidth: "400px",
		boxShadow: theme.shadows.default,
		outline: "none",
		display: "flex",
		flexDirection: "column",
	});

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!payor.trim()) {
			newErrors.payor = "Payor is required";
		}

		const phoneError = validatePhone(payorPhoneNumber);
		if (phoneError) {
			newErrors.payorPhoneNumber = phoneError;
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) return;

		await onSave({
			payor: payor.trim(),
			memberId: memberId.trim(),
			planType: planType || undefined,
			planName: planName.trim() || undefined,
			specialistRefsReqPriorAuth:
				specialistRefsReqPriorAuth === "true"
					? true
					: specialistRefsReqPriorAuth === "false"
						? false
						: undefined,
			payorPhoneNumber: payorPhoneNumber.trim() || undefined,
		});
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Escape" && !isSaving) {
			onClose();
		} else if (e.key === "Enter" && !isSaving) {
			void handleSave();
		}
	};

	return (
		<Modal
			isDismissable={!isSaving}
			isOpen={isOpen}
			onOpenChange={(open) => !open && !isSaving && onClose()}
			className={modalClassName}
		>
			<Dialog className={dialogClassName} aria-label="Edit insurance">
				<div onKeyDown={handleKeyDown}>
					<Flex column gap="lg" padding="lg">
						<Flex align="center" justify="space-between">
							<Text size="lg" weight="semibold" color="sand12">
								{title}
							</Text>
							<Button
								variant="ghost"
								size="xs"
								onPress={isSaving ? undefined : onClose}
							>
								<Icon name="X" size="sm" color="sand11" />
							</Button>
						</Flex>

						<Flex column gap="md">
							<Select
								label="Payor Name"
								aria-label="Payor name"
								value={payor}
								onSelectionChange={(value) => {
									setPayor(value ?? "");
									setErrors((prev) => ({ ...prev, payor: "" }));
								}}
								options={payorOptions()}
								placeholder="Select payor"
								size="sm"
								error={errors.payor}
							/>

							<TextField
								label="Plan Name"
								value={planName}
								onChange={(e) => setPlanName(e.target.value)}
								placeholder="e.g., Blue Advantage HMO"
							/>

							<TextField
								label="Member ID"
								value={memberId}
								onChange={(e) => setMemberId(e.target.value)}
								placeholder="Member ID"
							/>

							<TextField
								label="Payor Phone Number"
								value={payorPhoneNumber}
								onChange={(e) => {
									setPayorPhoneNumber(e.target.value);
									setErrors((prev) => ({ ...prev, payorPhoneNumber: "" }));
								}}
								placeholder="e.g., (800) 555-1234"
								error={errors.payorPhoneNumber}
							/>

							<Select
								label="Plan Type"
								aria-label="Plan type"
								value={planType}
								onSelectionChange={(value) => setPlanType(value ?? "")}
								options={PLAN_TYPES}
								placeholder="Select plan type"
								size="sm"
							/>

							<Select
								label="Referrals Require Prior Auth"
								aria-label="Referrals require prior auth"
								value={specialistRefsReqPriorAuth}
								onSelectionChange={(value) =>
									setSpecialistRefsReqPriorAuth(value ?? "")
								}
								options={PRIOR_AUTH_OPTIONS}
								placeholder="Select"
								size="sm"
							/>
						</Flex>

						<Flex gap="md" justify="space-between">
							{onDelete ? (
								<Button
									variant="outline"
									onPress={isSaving ? undefined : () => void onDelete()}
								>
									<Icon name="Trash2" size="sm" color="red11" />
									<Text size="sm" color="red11">
										Delete
									</Text>
								</Button>
							) : (
								<div />
							)}
							<Flex gap="md">
								<Button
									variant="outline"
									onPress={isSaving ? undefined : onClose}
								>
									Cancel
								</Button>
								<Button
									variant="primary"
									onPress={isSaving ? undefined : () => void handleSave()}
								>
									{isSaving ? "Saving..." : "Save"}
								</Button>
							</Flex>
						</Flex>
					</Flex>
				</div>
			</Dialog>
		</Modal>
	);
};
